import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Box } from '@mui/material';
import jsMind from 'jsmind';
import 'jsmind/style/jsmind.css';
import { ExercisePattern, PatternContainer } from '../types';

interface PatternMindMapProps {
  patterns: ExercisePattern[];
  containers: PatternContainer[];
  searchQuery?: string;
  onPatternClick: (pattern: ExercisePattern) => void;
  onContainerClick?: (container: PatternContainer) => void;
  onExpandModeChange?: (isActive: boolean) => void;
  onCollapseSiblingsModeChange?: (isActive: boolean) => void;
}

export interface PatternMindMapRef {
  collapseAll: () => void;
  enableExpandMode: () => void;
  enableCollapseSiblingsMode: () => void;
}

const PatternMindMap = forwardRef<PatternMindMapRef, PatternMindMapProps>(({
  patterns,
  containers,
  searchQuery = '',
  onPatternClick,
  onContainerClick,
  onExpandModeChange,
  onCollapseSiblingsModeChange,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const jmRef = useRef<any>(null);
  const scrollPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 }); // Track scroll position
  const expandModeRef = useRef<boolean>(false); // Track if we're in expand mode
  const collapseSiblingsModeRef = useRef<boolean>(false); // Track if we're in collapse siblings mode

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    collapseAll: () => {
      if (jmRef.current) {
        // Get all nodes and collapse them except root
        const allNodes = jmRef.current.get_data('node_tree').data;
        collapseNodeRecursive(allNodes);
      }
    },
    enableExpandMode: () => {
      expandModeRef.current = true;
      collapseSiblingsModeRef.current = false;
      onExpandModeChange?.(true);
      onCollapseSiblingsModeChange?.(false);
    },
    enableCollapseSiblingsMode: () => {
      collapseSiblingsModeRef.current = true;
      expandModeRef.current = false;
      onExpandModeChange?.(false);
      onCollapseSiblingsModeChange?.(true);
    }
  }));

  const collapseNodeRecursive = (node: any) => {
    if (!jmRef.current || !node) return;

    // Collapse children first (depth-first)
    if (node.children && node.children.length > 0) {
      node.children.forEach((child: any) => {
        collapseNodeRecursive(child);
        // Don't collapse root node
        if (child.id !== 'root') {
          try {
            jmRef.current.collapse_node(child.id);
          } catch (e) {
            // Ignore errors for nodes that can't be collapsed
          }
        }
      });
    }
  };

  const setNodeExpandedRecursive = (node: any) => {
    if (!node) return;

    // Set expanded property on this node
    node.expanded = true;

    // Then set expanded on all children recursively
    if (node.children && node.children.length > 0) {
      node.children.forEach((child: any) => {
        setNodeExpandedRecursive(child);
      });
    }
  };

  const setNodeCollapsedRecursive = (node: any) => {
    if (!node) return;

    // Set expanded to false on this node
    node.expanded = false;

    // Then collapse all children recursively
    if (node.children && node.children.length > 0) {
      node.children.forEach((child: any) => {
        setNodeCollapsedRecursive(child);
      });
    }
  };

  const findNodeById = (node: any, nodeId: string): any => {
    if (node.id === nodeId) return node;

    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        const found = findNodeById(child, nodeId);
        if (found) return found;
      }
    }

    return null;
  };

  const findParentNode = (rootNode: any, nodeId: string): any => {
    if (!rootNode || !rootNode.children) return null;

    for (const child of rootNode.children) {
      if (child.id === nodeId) {
        return rootNode;
      }
      const parent = findParentNode(child, nodeId);
      if (parent) return parent;
    }

    return null;
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // Wait for container to have proper dimensions before creating jsMind
    const initJsMind = () => {
      if (!containerRef.current) return;


    // Build container map
    const containerMap = new Map<number, PatternContainer>();
    containers.forEach(c => containerMap.set(c.id, c));

    // Create mapping from node ID to data
    const nodeDataMap = new Map<string, { type: 'pattern' | 'container', id: number }>();

    // Build mind map data
    const mindData: any = {
      meta: {
        name: 'Pattern Tree',
        version: '1.0',
      },
      format: 'node_tree',
      data: {
        id: 'root',
        topic: 'Patterns d\'exercices',
        expanded: true,
        'background-color': '#1976d2',
        'foreground-color': '#ffffff',
        children: [],
      },
    };

    // Group patterns by container
    const patternsByContainer = new Map<number | null, ExercisePattern[]>();
    patterns.forEach(p => {
      const key = p.container || null;
      if (!patternsByContainer.has(key)) {
        patternsByContainer.set(key, []);
      }
      patternsByContainer.get(key)!.push(p);
    });

    // Helper function to check if text matches search query
    const matchesSearch = (text: string): boolean => {
      if (!searchQuery) return false;
      return text.toLowerCase().includes(searchQuery.toLowerCase());
    };

    // Helper function to build a container node recursively
    const buildContainerNode = (container: PatternContainer): any => {
      const containerNodeId = `container-${container.id}`;
      nodeDataMap.set(containerNodeId, { type: 'container', id: container.id });

      const isMatch = matchesSearch(container.title);

      const containerNode: any = {
        id: containerNodeId,
        topic: container.title,
        expanded: false,
        'background-color': isMatch ? '#00ff00' : '#ff9800',
        'foreground-color': isMatch ? '#000000' : '#ffffff',
        children: [],
      };

      // Add child containers
      const childContainers = containers.filter(c => c.parent === container.id);
      childContainers.forEach(childContainer => {
        const childNode = buildContainerNode(childContainer);
        containerNode.children.push(childNode);
      });

      // Add patterns in this container
      const containerPatterns = patternsByContainer.get(container.id) || [];
      containerPatterns.forEach(pattern => {
        const patternNodeId = `pattern-${pattern.id}`;
        nodeDataMap.set(patternNodeId, { type: 'pattern', id: pattern.id });
        const isPatternMatch = matchesSearch(pattern.title);
        containerNode.children.push({
          id: patternNodeId,
          topic: pattern.title,
          expanded: true,
          'background-color': isPatternMatch ? '#ffff00' : '#2196f3',
          'foreground-color': isPatternMatch ? '#000000' : '#ffffff',
        });
      });

      return containerNode;
    };

    // Build root containers (those without parent or parent = null)
    const rootContainerList = containers.filter(c => !c.parent || c.parent === null);
    rootContainerList.forEach(container => {
      const containerNode = buildContainerNode(container);
      mindData.data.children.push(containerNode);
    });

    // Add root patterns (no container)
    const rootPatterns = patternsByContainer.get(null) || [];
    rootPatterns.forEach(pattern => {
      const nodeId = `pattern-${pattern.id}`;
      nodeDataMap.set(nodeId, { type: 'pattern', id: pattern.id });
      const isPatternMatch = matchesSearch(pattern.title);
      mindData.data.children.push({
        id: nodeId,
        topic: pattern.title,
        expanded: true,
        'background-color': isPatternMatch ? '#ffff00' : '#2196f3',
        'foreground-color': isPatternMatch ? '#000000' : '#ffffff',
      });
    });

    // Initialize jsMind
    const options = {
      container: containerRef.current,
      editable: false,
      theme: 'primary',
      view: {
        engine: 'canvas',
        hmargin: 150,
        vmargin: 80,
        line_width: 2,
        line_color: '#666',
      },
      layout: {
        hspace: 80,
        vspace: 40,
        pspace: 15,
      },
    };

    // Clean up previous instance
    if (jmRef.current) {
      // Save scroll position before destroying
      const container = containerRef.current;
      if (container) {
        scrollPositionRef.current = {
          x: container.scrollLeft,
          y: container.scrollTop,
        };

        // Clear the container completely
        container.innerHTML = '';
      }
      jmRef.current = null;
    }

    // Create new jsMind instance
    const jm = new (jsMind as any)(options);
    jm.show(mindData);
    jmRef.current = jm;

    // Restore scroll position after render
    const restoreScroll = () => {
      const targetScroll = scrollPositionRef.current;
      const container = containerRef.current;
      if (container && targetScroll) {
        container.scrollLeft = targetScroll.x;
        container.scrollTop = targetScroll.y;
      }
    };

    // Apply scroll after render
    setTimeout(restoreScroll, 100);

    // Add click handler using jsMind's event system
    const handleNodeClick = (nodeId: string) => {
      if (nodeId && nodeId !== 'root' && nodeDataMap.has(nodeId)) {
        const nodeData = nodeDataMap.get(nodeId)!;

        if (nodeData.type === 'pattern') {
          const pattern = patterns.find(p => p.id === nodeData.id);
          if (pattern) {
            onPatternClick(pattern);
          }
        } else if (nodeData.type === 'container' && onContainerClick) {
          const container = containers.find(c => c.id === nodeData.id);
          if (container) {
            onContainerClick(container);
          }
        }
      }
    };

    // Use DOM event listener on the container
    const handleClick = (event: any) => {
      // Find the jmnode element in the click path
      let target = event.target as HTMLElement;
      let jmnodeElement = null;

      // Traverse up to find jmnode element
      while (target && target !== containerElement) {
        if (target.tagName && target.tagName.toLowerCase() === 'jmnode') {
          jmnodeElement = target;
          break;
        }
        target = target.parentElement as HTMLElement;
      }

      if (jmnodeElement) {
        const nodeId = jmnodeElement.getAttribute('nodeid');
        if (nodeId) {
          // Check if we're in expand mode and this is a container
          if (expandModeRef.current && nodeDataMap.has(nodeId)) {
            const nodeData = nodeDataMap.get(nodeId)!;

            if (nodeData.type === 'container') {
              // Prevent ALL event propagation including to other handlers on the same element
              event.preventDefault();
              event.stopPropagation();
              event.stopImmediatePropagation();

              // Find the node in the tree and expand it recursively
              if (jmRef.current) {
                const jm = jmRef.current; // Capture reference before setTimeout
                const mindData = jm.get_data('node_tree');
                const targetNode = findNodeById(mindData.data, nodeId);
                if (targetNode) {
                  // Collect all node IDs to expand (depth-first order)
                  const nodesToExpand: string[] = [];
                  const collectNodeIds = (node: any) => {
                    if (node && node.id !== 'root') {
                      nodesToExpand.push(node.id);
                    }
                    if (node.children && node.children.length > 0) {
                      node.children.forEach((child: any) => collectNodeIds(child));
                    }
                  };
                  collectNodeIds(targetNode);

                  // Get the mind data (this returns a copy, not a reference)
                  const updatedMindData = jm.get_data('node_tree');

                  // Find the node in the COPIED data and modify it there
                  const nodeToExpand = findNodeById(updatedMindData.data, nodeId);
                  if (nodeToExpand) {
                    setNodeExpandedRecursive(nodeToExpand);
                  }

                  // Save scroll position
                  const container = containerRef.current;
                  if (container) {
                    scrollPositionRef.current = {
                      x: container.scrollLeft,
                      y: container.scrollTop,
                    };

                    // Completely destroy the current instance
                    container.innerHTML = '';
                    jmRef.current = null;

                    // Create a fresh jsMind instance with updated data
                    const options = {
                      container: container,
                      editable: false,
                      theme: 'primary',
                      view: {
                        engine: 'canvas',
                        hmargin: 150,
                        vmargin: 80,
                        line_width: 2,
                        line_color: '#666',
                      },
                      layout: {
                        hspace: 80,
                        vspace: 40,
                        pspace: 15,
                      },
                    };

                    const newJm = new (jsMind as any)(options);
                    newJm.show(updatedMindData);
                    jmRef.current = newJm;

                    // Restore scroll position after a short delay
                    setTimeout(() => {
                      if (container && scrollPositionRef.current) {
                        container.scrollLeft = scrollPositionRef.current.x;
                        container.scrollTop = scrollPositionRef.current.y;
                      }
                    }, 50);
                  }
                }
              }
              // Disable expand mode after the first container click
              expandModeRef.current = false;
              onExpandModeChange?.(false);
              return; // Don't trigger the normal click handler
            }
          }

          // Check if we're in collapse siblings mode
          if (collapseSiblingsModeRef.current && nodeDataMap.has(nodeId)) {
            const nodeData = nodeDataMap.get(nodeId)!;

            if (nodeData.type === 'container') {
              // Prevent ALL event propagation
              event.preventDefault();
              event.stopPropagation();
              event.stopImmediatePropagation();

              // Find parent node and collapse siblings
              if (jmRef.current) {
                const jm = jmRef.current;
                const updatedMindData = jm.get_data('node_tree');

                // Build path from root to clicked node
                const pathToClickedNode = new Set<string>();
                const buildPath = (node: any, targetId: string, currentPath: string[]): boolean => {
                  if (!node) return false;

                  if (node.id === targetId) {
                    // Found target - add all nodes in current path
                    currentPath.forEach(id => pathToClickedNode.add(id));
                    pathToClickedNode.add(node.id);
                    return true;
                  }

                  if (node.children && node.children.length > 0) {
                    currentPath.push(node.id);
                    for (const child of node.children) {
                      if (buildPath(child, targetId, currentPath)) {
                        return true;
                      }
                    }
                    currentPath.pop();
                  }

                  return false;
                };

                buildPath(updatedMindData.data, nodeId, []);

                // Collapse everything except the path to clicked node
                const collapseExceptPath = (node: any) => {
                  if (!node) return;

                  // If this node is in the path, keep it expanded
                  // Otherwise, collapse it
                  if (pathToClickedNode.has(node.id) || node.id === 'root') {
                    node.expanded = true;
                  } else {
                    node.expanded = false;
                  }

                  // Process children
                  if (node.children && node.children.length > 0) {
                    node.children.forEach((child: any) => collapseExceptPath(child));
                  }
                };

                collapseExceptPath(updatedMindData.data);

                // Find the parent and collapse all siblings
                const parentNode = findParentNode(updatedMindData.data, nodeId);
                if (parentNode && parentNode.children) {
                  parentNode.children.forEach((sibling: any) => {
                    if (sibling.id !== nodeId && sibling.id !== 'root') {
                      setNodeCollapsedRecursive(sibling);
                    } else if (sibling.id === nodeId) {
                      sibling.expanded = true;
                    }
                  });
                }

                // Recreate jsMind with updated data
                const container = containerRef.current;
                if (container) {
                  scrollPositionRef.current = {
                    x: container.scrollLeft,
                    y: container.scrollTop,
                  };

                  container.innerHTML = '';
                  jmRef.current = null;

                  const options = {
                    container: container,
                    editable: false,
                    theme: 'primary',
                    view: {
                      engine: 'canvas',
                      hmargin: 150,
                      vmargin: 80,
                      line_width: 2,
                      line_color: '#666',
                    },
                    layout: {
                      hspace: 80,
                      vspace: 40,
                      pspace: 15,
                    },
                  };

                  const newJm = new (jsMind as any)(options);
                  newJm.show(updatedMindData);
                  jmRef.current = newJm;

                  setTimeout(() => {
                    if (container && scrollPositionRef.current) {
                      container.scrollLeft = scrollPositionRef.current.x;
                      container.scrollTop = scrollPositionRef.current.y;
                    }
                  }, 50);
                }
              }

              // Disable collapse siblings mode after the first container click
              collapseSiblingsModeRef.current = false;
              onCollapseSiblingsModeChange?.(false);
              return; // Don't trigger the normal click handler
            }
          }

          handleNodeClick(nodeId);
        }
      }
    };

    const containerElement = containerRef.current;
    if (containerElement) {
      // Use capture phase to intercept clicks before jsMind processes them
      containerElement.addEventListener('click', handleClick, true);
    }

    return () => {
      if (containerElement) {
        containerElement.removeEventListener('click', handleClick, true);
      }
      if (jmRef.current) {
        jmRef.current = null;
      }
    };
    }; // End of initJsMind

    // Use setTimeout to ensure container has dimensions
    setTimeout(initJsMind, 100);

    return () => {
      if (jmRef.current) {
        jmRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patterns, containers, searchQuery, onPatternClick, onContainerClick]);

  return (
    <Box
      key={searchQuery} // Force re-mount when search changes
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'auto',
        backgroundColor: '#fafafa',
        '& jmnodes': {
          cursor: 'pointer',
        },
        '& jmnode': {
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8,
          },
        },
      }}
    />
  );
});

PatternMindMap.displayName = 'PatternMindMap';

export default PatternMindMap;
